import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChevronRight,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ArrowLeft,
  BookOpen,
  Clock,
  Phone,
  Mail,
  CheckCircle2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import TopBar from "../components/TopBar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { buildBreadcrumb, buildFaq, orgReference, SITE_URL } from "../lib/seoData";
import { getArticleBySlug, getAllArticles } from "../data/knowledgeCenterData";

const ArticlePage = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  const allArticles = getAllArticles();

  const currentIndex = allArticles.findIndex((a) => a.slug === slug);
  const prev = currentIndex > 0 ? allArticles[currentIndex - 1] : null;
  const next =
    currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/knowledge-center" className="text-[#8B1538] hover:underline">
            Return to Knowledge Center
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={article.metaTitle}
        description={article.metaDescription}
        path={`/knowledge-center/${article.slug}`}
        keywords={`${article.title}, ${article.category}, UV LED inks coatings`}
        jsonLd={[
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Knowledge Center", path: "/knowledge-center" },
            { name: article.title, path: `/knowledge-center/${article.slug}` },
          ]),
          {
            "@type": "Article",
            headline: article.title,
            description: article.metaDescription,
            url: `${SITE_URL}/knowledge-center/${article.slug}`,
            articleSection: article.category,
            author: orgReference,
            publisher: orgReference,
          },
          buildFaq(article.faqs),
        ]}
      />
      <TopBar />

      {/* Header */}
      <header className="fixed top-10 left-0 right-0 z-40 bg-white shadow-lg py-3">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="/crimson-logo-with-name.png"
                alt="Crimson Inks"
                className="h-14 w-auto object-contain"
              />
            </Link>
            <nav className="hidden lg:flex items-center space-x-2 text-sm">
              <Link to="/" className="text-gray-500 hover:text-[#8B1538] transition-colors">
                Home
              </Link>
              <ChevronRight className="h-4 w-4 text-gray-400" />
              <Link to="/knowledge-center" className="text-gray-500 hover:text-[#8B1538] transition-colors">
                Knowledge Center
              </Link>
              <ChevronRight className="h-4 w-4 text-gray-400" />
              <span className="text-[#8B1538] font-medium truncate max-w-[220px]">
                {article.title}
              </span>
            </nav>
            <Link to="/#contact">
              <Button className="bg-[#8B1538] hover:bg-[#DC143C] text-white px-6 py-2 rounded-md font-semibold hidden sm:flex">
                Contact Us
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Back */}
      <div className="pt-28 pb-4 bg-white border-b">
        <div className="container mx-auto px-4 lg:px-8">
          <Link
            to="/knowledge-center"
            className="inline-flex items-center text-gray-600 hover:text-[#8B1538] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Knowledge Center
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-[#8B1538] rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex items-center bg-[#8B1538] px-4 py-2 rounded-full text-sm font-medium">
                <BookOpen className="h-4 w-4 mr-2" />
                {article.category}
              </span>
              <span className="flex items-center text-sm text-gray-400">
                <Clock className="h-4 w-4 mr-1" />
                {article.readTime}
              </span>
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold mb-6" data-testid="article-title">
              {article.title}
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed max-w-3xl">
              {article.heroIntro}
            </p>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="py-12">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-10">
            {article.sections.map((section, i) => (
              <section key={i} data-testid={`article-section-${i}`}>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {section.heading}
                </h2>
                {section.body &&
                  section.body.map((para, j) => (
                    <p key={j} className="text-gray-700 leading-relaxed mb-4">
                      {para}
                    </p>
                  ))}
                {section.list && (
                  <ul className="space-y-2 mb-4">
                    {section.list.map((item, k) => (
                      <li key={k} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-[#8B1538] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {section.table && (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 mb-4">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-100 text-gray-900">
                        <tr>
                          {section.table.headers.map((h, hi) => (
                            <th key={hi} className="px-4 py-3 font-semibold">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {section.table.rows.map((row, ri) => (
                          <tr key={ri} className="bg-white">
                            {row.map((cell, ci) => (
                              <td key={ci} className="px-4 py-3 text-gray-700">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}

            {/* FAQ */}
            <section data-testid="article-faq">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {article.faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left text-gray-900 font-semibold">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            {/* Crimson Perspective */}
            <section className="rounded-xl border-2 border-[#8B1538] bg-gradient-to-br from-[#8B1538]/5 to-white p-6">
              <div className="flex items-center mb-3">
                <img
                  src="/crimson-logo.png"
                  alt="Crimson Inks"
                  className="h-8 w-8 mr-3 rounded-full"
                  onError={(e) => (e.target.style.display = "none")}
                />
                <h2 className="text-xl font-bold text-[#8B1538]">
                  The Crimson Inks Perspective
                </h2>
              </div>
              <p className="text-gray-800 leading-relaxed">
                {article.crimsonPerspective}
              </p>
              {article.relatedLinks && (
                <div className="flex flex-wrap gap-3 mt-5">
                  {article.relatedLinks.map((link, i) => (
                    <Link
                      key={i}
                      to={link.to}
                      className="inline-flex items-center text-sm font-semibold text-[#8B1538] hover:text-[#DC143C] transition-colors"
                      data-testid={`related-link-${i}`}
                    >
                      {link.label}
                      <ArrowLeft className="h-4 w-4 ml-1 rotate-180" />
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </article>

      {/* Prev / Next */}
      <section className="py-8 bg-gray-100 border-t">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            {prev ? (
              <Link
                to={`/knowledge-center/${prev.slug}`}
                className="flex items-center text-gray-600 hover:text-[#8B1538] transition-colors group"
              >
                <ChevronLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
                <div className="text-left">
                  <span className="text-xs text-gray-400 block">Previous</span>
                  <span className="font-medium line-clamp-1">{prev.title}</span>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {next ? (
              <Link
                to={`/knowledge-center/${next.slug}`}
                className="flex items-center text-gray-600 hover:text-[#8B1538] transition-colors group text-right"
              >
                <div>
                  <span className="text-xs text-gray-400 block">Next</span>
                  <span className="font-medium line-clamp-1">{next.title}</span>
                </div>
                <ChevronRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </section>

      {/* Sticky CTA */}
      <section className="py-12 bg-gradient-to-r from-[#8B1538] to-[#DC143C] sticky bottom-0 z-30">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white text-center md:text-left">
              <h3 className="text-xl font-bold">Looking for the right ink or coating?</h3>
              <p className="text-white/80">Talk to the Crimson Inks technical team.</p>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="tel:+917706994400"
                className="inline-flex items-center bg-white text-[#8B1538] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                <Phone className="h-5 w-5 mr-2" />
                Call Now
              </a>
              <a
                href="mailto:Crimson.inks7475@gmail.com"
                className="inline-flex items-center bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors"
              >
                <Mail className="h-5 w-5 mr-2" />
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArticlePage;
